<script setup lang="ts">
import Item from "@/components/ui/item/Item.vue";
import ItemContent from "@/components/ui/item/ItemContent.vue";
import ItemDescription from "@/components/ui/item/ItemDescription.vue";
import ItemMedia from "@/components/ui/item/ItemMedia.vue";
import ItemTitle from "@/components/ui/item/ItemTitle.vue";
import Label from "@/components/ui/label/Label.vue";
import Toggle from "@/components/ui/toggle/Toggle.vue";
import { Laptop2Icon } from "lucide-vue-next";
import type { PropType } from "vue";

const props = defineProps({
  seats: {
    type: Array as PropType<{ row: string[] }[]>,
    required: true,
    default: () => [
      { row: ["", "", "space", "", ""] },
      { row: ["", "", "space", "", ""] },
      { row: ["", "", "space", "", ""] },
    ],
  },
  bookedSeat: {
    type: Array,
    required: true,
    default: () => [null, null],
  },
});
</script>

<template>
  <div
    class="flex w-full max-w-lg flex-col gap-4 justify-center items-center lg:max-w-100"
  >
    <Item class="flex items-center justify-center lg:py-0">
      <ItemMedia variant="icon" class="border-0 bg-transparent">
        <div
          class="icon-container flex items-center justify-center w-8 h-8 rounded-full bg-accent dark:bg-accent"
        >
          <Laptop2Icon class="w-4 h-4" />
        </div>
      </ItemMedia>
      <div class="flex flex-col">
        <ItemContent>
          <ItemTitle> Select your spot </ItemTitle>
        </ItemContent>
        <ItemContent>
          <ItemDescription
            class="text-muted-foreground dark:text-muted-foreground text-xs"
          >
            Pick your preferred spot at this event.
          </ItemDescription>
        </ItemContent>
      </div>
    </Item>
    <div class="grid grid-cols-3 items-center text-sm gap-2 px-6">
      <div class="flex items-center gap-2">
        <div class="size-4 border-gray-500 border-2 rounded-xs"></div>

        <Label>Available</Label>
      </div>
      <div class="flex items-center gap-2">
        <div class="h-4 w-4 bg-gray-500 rounded-xs"></div>
        <Label>Seated</Label>
      </div>
      <div class="flex items-center gap-2">
        <div class="h-4 w-4 bg-primary rounded-xs"></div>
        <Label>Selected</Label>
      </div>
    </div>
    <div class="bg-secondary w-full h-full px-5 pt-2 rounded-xl lg:w-75">
      <table
        class="table-auto w-full text-sm text-left text-gray-500 dark:text-gray-400"
      >
        <thead class="text-xs text-gray-700 uppercase dark:text-gray-400">
          <tr>
            <th scope="col" class="px-4 py-2 text-center">A</th>
            <th scope="col" class="px-4 py-2 text-center">B</th>
            <th scope="col" class="px-4 py-2 text-center w-full"></th>
            <th scope="col" class="px-4 py-2 text-center">C</th>
            <th scope="col" class="px-4 py-2 text-center">D</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(rowObj, rowIndex) in seats" :key="rowIndex">
            <td
              v-for="(status, colIndex) in rowObj.row"
              :key="colIndex"
              class="py-1 text-center"
            >
              <!-- {{ status }} -->
              <div v-if="status === 'space'">{{ rowIndex }}</div>
              <div v-else>
                <Toggle
                  v-if="
                    bookedSeat[0] === rowIndex && bookedSeat[1] === colIndex
                  "
                  class="bg-primary hover:bg-primary/50"
                  size="sm"
                  @click="() => $emit('onClickedOnSelected')"
                >
                </Toggle>
                <Toggle
                  v-else
                  :id="`seat-${rowIndex}-${colIndex}`"
                  :class="{
                    'bg-gray-500': status !== '',
                    'border-2 border-gray-500': status === '',
                  }"
                  size="sm"
                  :aria-label="`Toggle Row ${
                    rowIndex + 1
                  } Col ${String.fromCharCode(
                    65 + (colIndex < 2 ? colIndex : colIndex + 1)
                  )}`"
                  :disabled="status !== ''"
                  @click="
                    (value) =>
                      $emit('onSeatChange', {
                        rowIndex,
                        colIndex,
                      })
                  "
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
